import { makeAutoObservable, runInAction } from 'mobx';
import { forEach, omit } from 'lodash';
import { userStore } from 'stores';
import { getDayNumberInYear, getDaysQtyInYear, getVacationHours } from 'utils/timeUtils';
import api from '../services';
import { PROJECTS } from 'const';

export class ReportStore {
  constructor(OAuthService) {
    this.service = OAuthService;
    this.loading = false;
    this.timeFills = {};
    this.originalTimeFills = {};
    this.timeFillsByUser = {};
    this.originalTimeFillsByUser = {};
    this.supervisorMode = false;
    this.vacationDaysLeft = false;
    this.year = new Date().getFullYear();
    this.project = PROJECTS[0].label;
    makeAutoObservable(this);
  }

  loadTimeFills = async () => {
    runInAction(() => {
      this.loading = true;
    });

    try {
      await userStore.loadUsers(this.supervisorMode);
      const result = await this.service.fetchFills(userStore.user.email, this.supervisorMode, this.project);
      const timeFills = {};
      const timeFillsByUser = {};
      result.forEach((item) => {
        const timeFill = {
          duration: item.duration,
          projectName: item.project_name,
          projectType: item.project_type,
          id: item.id,
          user: userStore.getUserNameByEmail(item.user_email) || item.user_email,
          userEmail: item.user_email,
        };
        if (!timeFills[item.date]) {
          timeFills[item.date] = [timeFill];
        } else {
          timeFills[item.date].push(timeFill);
        }

        const email = item.user_email;
        const monthYear = item.date.slice(0, 7); // "2024-05-21" -> 2024-05"
        const durationNum = parseFloat(item.duration);

        if (!timeFillsByUser[email]) {
          timeFillsByUser[email] = {
            [monthYear]: {
              [item.project_type]: durationNum,
            },
          };
        } else if (!timeFillsByUser[email][monthYear]) {
          timeFillsByUser[email][monthYear] = {
            [item.project_type]: durationNum,
          };
        } else if (!timeFillsByUser[email][monthYear][item.project_type]) {
          timeFillsByUser[email][monthYear][item.project_type] = durationNum;
        } else {
          timeFillsByUser[email][monthYear][item.project_type] += durationNum;
        }
      });
      runInAction(() => {
        this.originalTimeFills = timeFills;
        this.timeFills = timeFills;
        this.timeFillsByUser = timeFillsByUser;
        this.originalTimeFillsByUser = timeFillsByUser;
      });

      this.calculateVacations();
    } catch (err) {
      console.log(err);
    }
    runInAction(() => {
      this.loading = false;
    });
  };

  postTimeFills = async (fill, email) => {
    this.setLoading(true);
    try {
      const posted = await this.service.postFill(fill, email);
      // const posted = await this.service.postFill(fill, 'yevhen.pushkarov@saldvin.com');
      if (posted) {
        await this.loadTimeFills();
      }
    } catch (err) {
      console.log(err);
    }
    this.setLoading(false);
    return this.timeFills;
  };

  deleteTimeFill = async (id) => {
    this.setLoading(true);
    try {
      const posted = await this.service.deleteFill(id, userStore.user.email);
      if (posted) {
        await this.loadTimeFills();
      }
    } catch (err) {
      console.log(err);
    }
    this.setLoading(false);
    return this.timeFills;
  };

  setLoading = (value) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  setProject = (value) => {
    runInAction(() => {
      this.project = value;
    });
  };

  setSupervisorMode = (isSupervisorMode) => {
    this.supervisorMode = isSupervisorMode;
    this.loadTimeFills();
  };

  filterTimeFills = (usersToExclude) => {
    const timeFills = {};
    const timeFillsByUser = {};
    const usersToExcludeEmailsSet = new Set(usersToExclude);

    forEach(this.originalTimeFills, (fills, date) => {
      timeFills[date] = fills;

      for (let index = fills.length - 1; index >= 0; index--) {
        const fill = fills[index];

        if (usersToExcludeEmailsSet.has(fill.userEmail)) {
          timeFills[date] = [...timeFills[date].slice(0, index), ...timeFills[date].slice(index + 1)];
        }
      }
    });

    forEach(this.originalTimeFillsByUser, (fills, email) => {
      if (!usersToExcludeEmailsSet.has(email)) {
        timeFillsByUser[email] = fills;
      }
    });

    runInAction(() => {
      this.timeFills = timeFills;
      this.timeFillsByUser = timeFillsByUser;
    });
  };

  calculateVacations = () => {
    const user = userStore.customUser || userStore.user;
    if (user?.startDate) {
      const startDate = new Date(user.startDate);
      const currentYearStr = String(this.year);
      let vacationsHoursUsed = 0;
      forEach(this.timeFills, (day, strDate) => {
        if (strDate.substring(0, 4) !== currentYearStr) {
          return;
        }
        const hoursForTheDay = day.reduce((acc, current) => {
          if (current.projectType === 'VACATION' && user.email === current.userEmail) {
            return acc + parseFloat(current.duration);
          }
          return acc;
        }, 0);
        vacationsHoursUsed += hoursForTheDay;
      });

      const defaultVacationHours = getVacationHours(user.email);

      let remainingVacationHours = defaultVacationHours;
      let remainingVacationHoursRounded = defaultVacationHours;
      if (startDate.getFullYear() === this.year) {
        const startDayInYear = getDayNumberInYear(startDate);
        const daysInYear = getDaysQtyInYear(this.year);
        remainingVacationHours = (defaultVacationHours / daysInYear) * (daysInYear - startDayInYear);
        const vacationDays =
          remainingVacationHours % 1 > 0.6
            ? Math.ceil(remainingVacationHours / 8)
            : Math.floor(remainingVacationHours / 8);
        remainingVacationHoursRounded = vacationDays * 8;
      }
      const vacationDays = (
        (remainingVacationHoursRounded + user.transferedVacationHours - vacationsHoursUsed) /
        8
      ).toFixed('1');

      runInAction(() => {
        this.vacationDaysLeft = vacationDays.slice(-1) === '0' ? vacationDays.slice(0, -2) : vacationDays;
      });
    }
  };
}

export const reportStore = new ReportStore(api.reportService);
